import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clearStoredTimestamp, readStoredTimestamp, writeStoredTimestamp } from './timer.utils';

const DEFAULT_RESEND_DELAY = 60;
const DEFAULT_TIMER_MODE = 'targetDate';

type TimerMode = 'targetDate' | 'countdown';

interface UseTimerOptions {
  mode?: TimerMode;
  targetDate?: Date | string | null;
  onExpire?: () => void;
  delay?: number;
  autoStart?: boolean;
  localStorageKey?: string;
}

interface TimeComponents {
  hours: number;
  minutes: number;
  seconds: number;
}

interface UseTimerReturn {
  timeRemaining: number | null;
  timeComponents: TimeComponents | null;
  isExpired: boolean;
  timer: number;
  isResendDisabled: boolean;
  restartTimer: () => void;
  setTimerTarget: (timestamp: number) => void;
}

export const useTimer = ({
  mode = DEFAULT_TIMER_MODE,
  targetDate,
  onExpire,
  delay = DEFAULT_RESEND_DELAY,
  autoStart = true,
  localStorageKey,
}: UseTimerOptions): UseTimerReturn => {
  const isCountdownMode = mode === 'countdown';
  const countdownStartsImmediately = isCountdownMode && autoStart;
  const initialNow = useRef(Date.now()).current;
  const [currentTime, setCurrentTime] = useState(initialNow);

  const getResendTargetTimestamp = useCallback(() => {
    if (localStorageKey) {
      const stored = readStoredTimestamp(localStorageKey);
      if (stored !== null) {
        return stored;
      }
    }
    return countdownStartsImmediately ? initialNow + delay * 1000 : null;
  }, [localStorageKey, countdownStartsImmediately, initialNow, delay]);

  const [resendTargetTimestamp, setResendTargetTimestamp] = useState<number | null>(() => getResendTargetTimestamp());
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  const targetTimestamp = useMemo(() => {
    if (isCountdownMode && resendTargetTimestamp !== null) {
      return resendTargetTimestamp;
    }

    if (!targetDate) {
      return null;
    }

    const target = targetDate instanceof Date ? targetDate : new Date(targetDate);
    const parsedTarget = target.getTime();

    return Number.isNaN(parsedTarget) ? null : parsedTarget;
  }, [targetDate, resendTargetTimestamp, isCountdownMode]);

  const timeRemaining = useMemo(() => {
    if (targetTimestamp === null) {
      return null;
    }

    const diffInMs = targetTimestamp - currentTime;

    return diffInMs > 0 ? diffInMs : 0;
  }, [targetTimestamp, currentTime]);

  const timeComponents = useMemo(() => {
    if (!timeRemaining || timeRemaining <= 0) {
      return null;
    }

    const totalSeconds = Math.floor(timeRemaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { hours, minutes, seconds };
  }, [timeRemaining]);

  const isExpired = timeRemaining === 0;
  const expiredTargetTimestampRef = useRef<number | null>(null);

  useEffect(() => {
    if (targetTimestamp === null) {
      expiredTargetTimestampRef.current = null;
      return;
    }

    if (!isExpired) {
      if (expiredTargetTimestampRef.current === targetTimestamp) {
        expiredTargetTimestampRef.current = null;
      }
      return;
    }

    if (expiredTargetTimestampRef.current !== targetTimestamp) {
      expiredTargetTimestampRef.current = targetTimestamp;
      onExpireRef.current?.();
      if (localStorageKey) {
        clearStoredTimestamp(localStorageKey);
      }
    }
  }, [isExpired, targetTimestamp, localStorageKey]);

  const timer = timeRemaining ? Math.ceil(timeRemaining / 1000) : 0;
  const isResendDisabled = timer > 0;

  const setTimerTarget = useCallback((timestamp: number) => {
    if (!isCountdownMode) {
      return;
    }
    setCurrentTime(Date.now());
    setResendTargetTimestamp(timestamp);
    if (localStorageKey) {
      writeStoredTimestamp(localStorageKey, timestamp);
    }
  }, [isCountdownMode, localStorageKey]);

  const restartTimer = useCallback(() => {
    if (!isCountdownMode) {
      return;
    }

    const now = Date.now();
    const target = now + delay * 1000;

    setCurrentTime(now);
    setResendTargetTimestamp(target);
    if (localStorageKey) {
      writeStoredTimestamp(localStorageKey, target);
    }
  }, [isCountdownMode, localStorageKey, delay]);

  useEffect(() => {
    if (targetTimestamp === null) {
      return;
    }
    const interval = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [targetTimestamp]);

  return {
    timeRemaining,
    timeComponents,
    isExpired,
    timer,
    isResendDisabled,
    restartTimer,
    setTimerTarget,
  };
};
